import {
  PIECE_WIDTH,
  GAME_H,
  INIT_SPEED,
  DRAWING_MODE_SINGLE,
  DRAWING_MODE_DOUBLE,
  GAME_W,
  TOTAL_DISTANCE,
  TERRAIN_COSINE,
  TERRAIN_FRACTAL,
  TERRAIN_MULTISINE,
  TERRAIN_PERLIN,
  COPTER_X_RATIO,
} from "./data";
import type { ITerrain } from "./types";

import { CosineTerrain, FractalTerrain, MultiSineTerrain, PerlinTerrain } from "./terrain-gen";
import { getImage } from "./images";
import { Asset } from "assets.gen";
import { gameState } from "./state";
import { Tid } from "i18n";
import { dt } from "lib/loop";

interface TerrainSection {
  canvas: OffscreenCanvas;
  worldX: number;
}

export const landscape = {
  vx: INIT_SPEED,
  deltaShift: 0,

  _sections: [] as TerrainSection[],
  _frame: null as OffscreenCanvas | null,
  _worldX: 0,
  _prevWorldX: 0,
  _mode: DRAWING_MODE_DOUBLE,
  _method: null as ITerrain | null,
  _tileAsset: "",
  _outlineAsset: "",
  _tilePattern: null as CanvasPattern | null,
  _outlinePattern: null as CanvasPattern | null,
  _lastPoint: 0,

  reset(): void {
    this.vx = INIT_SPEED;
    this._mode = level.drawingMode;
    this._method = createTerrainGenerator(level.drawingMethod);

    this._tileAsset = level.tileAsset;
    this._outlineAsset = level.outlineAsset;
    this._tilePattern = null;
    this._outlinePattern = null;
    this._lastPoint = 0;

    this._sections = [];
    this._worldX = 0;
    this._prevWorldX = 0;
    this._lastPoint = 0;

    if (!this._frame) {
      this._frame = new OffscreenCanvas(GAME_W, GAME_H);
    }

    const cameraOffset = (GAME_W * COPTER_X_RATIO) | 0;
    this.appendSection(-cameraOffset);
    this.appendSection(-cameraOffset + PIECE_WIDTH);
  },

  appendSection(worldX: number): void {
    const canvas = new OffscreenCanvas(PIECE_WIDTH, GAME_H);
    const section: TerrainSection = { canvas, worldX };
    this._sections.push(section);
    this.drawSection(section);
  },

  drawSection(section: TerrainSection): void {
    const ctx = section.canvas.getContext("2d")!;
    ctx.clearRect(0, 0, PIECE_WIDTH, GAME_H);

    if (!this._method) return;
    const points = this._method.generate(PIECE_WIDTH, GAME_H, this._lastPoint);
    const maxSize = this._method.maxSize;
    const step = PIECE_WIDTH / (points.length - 1);
    const margin = 8;
    const minGap = 40;

    for (let i = 0; i < points.length; i++) {
      if (points[i] > maxSize - margin) points[i] = maxSize - margin;
      if (points[i] < -maxSize + margin) points[i] = -maxSize + margin;
      if (this._mode === DRAWING_MODE_DOUBLE) {
        const minPoint = (2 * maxSize - GAME_H + minGap) / 2;
        if (points[i] < minPoint) points[i] = minPoint;
      }
    }

    this._lastPoint = points[points.length - 1];

    if (this._mode === DRAWING_MODE_DOUBLE) {
      this.drawTerrainFill(ctx, points, step, 0, 1, maxSize);
      this.drawTerrainOutline(ctx, points, step, 0, 1, maxSize);
    }
    this.drawTerrainFill(ctx, points, step, 0, -1, maxSize);
    this.drawTerrainOutline(ctx, points, step, 0, -1, maxSize);
  },

  update(): void {
    if (!gameState.started && !gameState.emulation) return;

    this._prevWorldX = this._worldX;
    this._worldX += this.vx * dt;

    this.deltaShift = (this._worldX - this._prevWorldX) | 0;
    if (this.deltaShift < 0) this.deltaShift = 0;
    if (this.deltaShift > PIECE_WIDTH) this.deltaShift = (this.vx * dt) | 0;
  },

  syncFrame(cameraX: number): void {
    const last = this._sections[this._sections.length - 1];
    if (last && last.worldX + PIECE_WIDTH < cameraX + GAME_W + PIECE_WIDTH) {
      this.appendSection(last.worldX + PIECE_WIDTH);
    }

    for (let i = this._sections.length - 1; i >= 0; i--) {
      if (this._sections[i].worldX + PIECE_WIDTH < cameraX) {
        this._sections.splice(0, i + 1);
        break;
      }
    }

    this.updateFrame(cameraX);
  },

  updateFrame(cameraX: number): void {
    if (!this._frame) return;
    const fctx = this._frame.getContext("2d")!;
    fctx.clearRect(0, 0, GAME_W, GAME_H);

    for (const section of this._sections) {
      const sx = section.worldX - cameraX;
      if (sx + PIECE_WIDTH <= 0) continue;
      if (sx >= GAME_W) break;
      fctx.drawImage(section.canvas, sx, 0);
    }
  },

  makeHole(wx: number, wy: number, r: number = 15): void {
    for (const section of this._sections) {
      const localX = wx - section.worldX;
      if (localX < -r || localX >= PIECE_WIDTH + r) continue;
      const pctx = section.canvas.getContext("2d")!;
      pctx.globalCompositeOperation = "destination-out";
      pctx.beginPath();
      pctx.arc(localX, wy, r, 0, 2 * Math.PI);
      pctx.fill();
      pctx.globalCompositeOperation = "source-over";
    }
  },

  getPlaceOffset(wx: number, dir: number): number {
    const offsets = this.getPlaceOffsets(wx);
    return dir > 0 ? offsets.top : offsets.bottom;
  },

  getPlaceOffsets(wx: number): { top: number; bottom: number } {
    const section = this.findSection(wx);
    if (!section) {
      return { top: 0, bottom: GAME_H - 1 };
    }
    const localX = (wx - section.worldX) | 0;
    return this._getColumn(section, localX) ?? { top: 0, bottom: GAME_H - 1 };
  },

  _getColumn(section: TerrainSection, localX: number): { top: number; bottom: number } | null {
    if (localX < 0 || localX >= PIECE_WIDTH) {
      return null;
    }

    const ctx = section.canvas.getContext("2d")!;

    try {
      const imageData = ctx.getImageData(localX, 0, 1, GAME_H);
      const data = imageData.data;

      if (this._mode === DRAWING_MODE_SINGLE) {
        let bottom = GAME_H - 1;
        for (let y = GAME_H - 1; y >= 0; y--) {
          if (data[y * 4 + 3] === 0) {
            bottom = y;
            break;
          }
        }
        return { top: 0, bottom };
      }

      return this._scanColumn(data);
    } catch {
      return null;
    }
  },

  _scanColumn(data: Uint8ClampedArray): { top: number; bottom: number } {
    let top = 0;
    let bottom = GAME_H - 1;
    for (let y = 0; y < GAME_H; y++) {
      if (data[y * 4 + 3] === 0) {
        top = y;
        break;
      }
    }
    for (let y = GAME_H - 1; y >= 0; y--) {
      if (data[y * 4 + 3] === 0) {
        bottom = y;
        break;
      }
    }
    return { top, bottom };
  },

  findSection(wx: number): TerrainSection | null {
    for (const section of this._sections) {
      if (wx >= section.worldX && wx < section.worldX + PIECE_WIDTH) {
        return section;
      }
    }
    return this._sections[this._sections.length - 1] ?? null;
  },

  getSafeRect(x: number, distance: number): { top: number; bottom: number; width: number } {
    let top = 0;
    let bottom = GAME_H;
    let width = 0;
    let ix = x;

    while (ix < x + distance && width < distance) {
      const section = this.findSection(ix);
      if (!section) break;

      const localStart = ((ix - section.worldX) | 0);
      const localEnd = Math.min(localStart + ((x + distance - ix) | 0), PIECE_WIDTH);
      const stripW = localEnd - localStart;

      if (stripW > 0 && localStart >= 0 && localStart < PIECE_WIDTH) {
        try {
          const ctx = section.canvas.getContext("2d")!;
          const imageData = ctx.getImageData(localStart, 0, stripW, GAME_H);
          const data = imageData.data;

          for (let col = 0; col < stripW; col += 4) {
            if (this._mode !== DRAWING_MODE_SINGLE) {
              const colTop = this._scanColumnFromData(data, col, stripW);
              if (colTop.top > top) top = colTop.top;
              if (colTop.bottom < bottom) bottom = colTop.bottom;
            } else {
              for (let y = GAME_H - 1; y >= 0; y--) {
                if (data[(y * stripW * 4) + col * 4 + 3] === 0) {
                  if (y < bottom) bottom = y;
                  break;
                }
              }
            }
            width = (ix + col) - x;
            if (bottom - top < 30) return { top, bottom, width };
          }
        } catch {
          break;
        }
      }
      ix += Math.max(stripW, 4);
    }

    return { top, bottom, width };
  },

  _scanColumnFromData(data: Uint8ClampedArray, col: number, rowStride: number): { top: number; bottom: number } {
    let top = 0;
    let bottom = GAME_H - 1;
    const base = col * 4;
    for (let y = 0; y < GAME_H; y++) {
      if (data[(y * rowStride * 4) + base + 3] === 0) {
        top = y;
        break;
      }
    }
    for (let y = GAME_H - 1; y >= 0; y--) {
      if (data[(y * rowStride * 4) + base + 3] === 0) {
        bottom = y;
        break;
      }
    }
    return { top, bottom };
  },

  drawTerrainFill(
    ctx: OffscreenCanvasRenderingContext2D,
    points: number[],
    step: number,
    startX: number,
    dir: number,
    maxSize: number,
  ): void {
    this.ensureTilePattern(ctx);
    const sy = dir > 0 ? 0 : GAME_H;

    ctx.beginPath();
    if (this._tilePattern) ctx.fillStyle = this._tilePattern;
    else ctx.fillStyle = "#333333";

    ctx.moveTo(startX, sy);
    for (let i = 0; i < points.length; i++) {
      const x = startX + i * step;
      const y = sy + dir * maxSize - points[i];
      ctx.lineTo(x, y);
    }
    ctx.lineTo(startX + (points.length - 1) * step, sy);
    ctx.closePath();
    ctx.fill();
  },

  drawTerrainOutline(
    ctx: OffscreenCanvasRenderingContext2D,
    points: number[],
    step: number,
    startX: number,
    dir: number,
    maxSize: number,
  ): void {
    this.ensureOutlinePattern(ctx);
    const sy = dir > 0 ? 0 : GAME_H;

    if (this._outlinePattern) {
      ctx.strokeStyle = this._outlinePattern;
      const img = getImage(this._outlineAsset);
      ctx.lineWidth = img ? img.height : 3;
    } else {
      ctx.strokeStyle = "#555555";
      ctx.lineWidth = 3;
    }

    ctx.beginPath();
    for (let i = 0; i < points.length; i++) {
      const x = startX + i * step;
      const y = sy + dir * maxSize - points[i];
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  },

  ensureTilePattern(ctx: OffscreenCanvasRenderingContext2D): void {
    if (this._tilePattern) return;
    const img = getImage(this._tileAsset);
    if (!img) return;
    this._tilePattern = ctx.createPattern(img, "repeat");
  },

  ensureOutlinePattern(ctx: OffscreenCanvasRenderingContext2D): void {
    if (this._outlinePattern) return;
    const img = getImage(this._outlineAsset);
    if (!img) return;
    this._outlinePattern = ctx.createPattern(img, "repeat");
  },
};

interface ZoneConfig {
  drawingMode: string;
  drawingMethod: string;
  tileAsset: string;
  outlineAsset: string;
  coins: boolean;
  turrets: boolean;
  turretInterval: number;
  coinInterval: number;
  birdInterval: number;
  speedMultiplier: number;
  stars: boolean;
}

const ZONES: ZoneConfig[] = [
  {
    drawingMode: DRAWING_MODE_SINGLE,
    drawingMethod: TERRAIN_FRACTAL,
    tileAsset: Asset.gfx_tile_mountain,
    outlineAsset: Asset.gfx_outline_grass,
    coins: true,
    turrets: false,
    turretInterval: 999,
    coinInterval: 8,
    birdInterval: 60,
    speedMultiplier: 1.0,
    stars: true,
  },
  {
    drawingMode: DRAWING_MODE_DOUBLE,
    drawingMethod: TERRAIN_COSINE,
    tileAsset: Asset.gfx_tile_sand,
    outlineAsset: Asset.gfx_outline_ground,
    coins: true,
    turrets: false,
    turretInterval: 999,
    coinInterval: 8,
    birdInterval: 50,
    speedMultiplier: 1.0,
    stars: true,
  },
  {
    drawingMode: DRAWING_MODE_SINGLE,
    drawingMethod: TERRAIN_MULTISINE,
    tileAsset: Asset.gfx_tile_stone,
    outlineAsset: Asset.gfx_outline_stone,
    coins: true,
    turrets: true,
    turretInterval: 80,
    coinInterval: 8,
    birdInterval: 45,
    speedMultiplier: 1.0,
    stars: true,
  },
  {
    drawingMode: DRAWING_MODE_DOUBLE,
    drawingMethod: TERRAIN_FRACTAL,
    tileAsset: Asset.gfx_tile_swamp,
    outlineAsset: Asset.gfx_outline_lava,
    coins: true,
    turrets: true,
    turretInterval: 60,
    coinInterval: 8,
    birdInterval: 40,
    speedMultiplier: 1.2,
    stars: true,
  },
  {
    drawingMode: DRAWING_MODE_SINGLE,
    drawingMethod: TERRAIN_PERLIN,
    tileAsset: Asset.gfx_tile_snow,
    outlineAsset: Asset.gfx_outline_snow,
    coins: true,
    turrets: true,
    turretInterval: 45,
    coinInterval: 8,
    birdInterval: 35,
    speedMultiplier: 1.4,
    stars: true,
  },
  {
    drawingMode: DRAWING_MODE_DOUBLE,
    drawingMethod: TERRAIN_FRACTAL,
    tileAsset: Asset.gfx_tile_mountain,
    outlineAsset: Asset.gfx_outline_grass,
    coins: true,
    turrets: true,
    turretInterval: 35,
    coinInterval: 6,
    birdInterval: 30,
    speedMultiplier: 1.6,
    stars: true,
  },
];

export const ZONE_DISTANCE = TOTAL_DISTANCE;

export const level = {
  drawingMode: DRAWING_MODE_SINGLE,
  drawingMethod: TERRAIN_FRACTAL as string,
  tileAsset: Asset.gfx_tile_mountain as string,
  outlineAsset: Asset.gfx_outline_grass as string,
  stars: true,
  coins: true,
  turrets: false,
  turretInterval: 999,
  coinInterval: 8,
  birdInterval: 60,
  speedMultiplier: 1.0,
  zoneDistance: TOTAL_DISTANCE,
};

export function nextZone(): void {
  const zoneIndex = gameState.zoneIndex % ZONES.length;
  const zone = ZONES[zoneIndex];
  level.drawingMode = zone.drawingMode;
  level.drawingMethod = zone.drawingMethod;
  level.tileAsset = zone.tileAsset;
  level.outlineAsset = zone.outlineAsset;
  level.stars = zone.stars;
  level.coins = zone.coins;
  level.turrets = zone.turrets;
  level.turretInterval = zone.turretInterval;
  level.coinInterval = zone.coinInterval;
  level.birdInterval = zone.birdInterval;
  level.speedMultiplier = zone.speedMultiplier;
  level.zoneDistance = TOTAL_DISTANCE;
  gameState.zoneIndex++;
}

export function getZoneNameTid(): number {
  const zoneIndex = (gameState.zoneIndex - 1 + ZONES.length) % ZONES.length;
  return Tid.zone_0 + zoneIndex;
}

function createTerrainGenerator(method: string): ITerrain {
  if (method === TERRAIN_COSINE) return new CosineTerrain();
  if (method === TERRAIN_PERLIN) return new PerlinTerrain();
  if (method === TERRAIN_MULTISINE) return new MultiSineTerrain();
  return new FractalTerrain();
}